import { KNOWN_POSITIONS } from '@opti-sea/core';
import { RISQ_STATUS_ICONS, type RisqStatus } from '@opti-sea/risq';
import { Alert, Anchor, Badge, Center, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

/** Status colours: the word and icon carry the meaning; colour only repeats it. */
const STATUS_COLOR: Record<RisqStatus, string> = {
  not_started: 'gray',
  in_progress: 'orange',
  closed: 'green',
  office_action: 'violet',
};

export function StatusBadge({ status }: { status: RisqStatus }) {
  const { t } = useTranslation(['dashboard', 'common']);
  return (
    <Badge color={STATUS_COLOR[status]} variant="light" size="lg" radius="sm" tt="none">
      {t('status.option', { icon: RISQ_STATUS_ICONS[status], word: t(`common:status.${status}`) })}
    </Badge>
  );
}

type KnownPositionCode = 'MASTER' | 'CO' | '2O' | '3O' | 'CE' | '2E' | '3E' | 'ETO';

function isKnownPosition(code: string): code is KnownPositionCode {
  return KNOWN_POSITIONS.some((p) => p.code === code);
}

/** Position names from the shared texts; an unknown code is shown as it is. */
export function usePositionNames() {
  const { t } = useTranslation('common');
  const name = useCallback(
    (code: string) => (isKnownPosition(code) ? t(`positions.${code}`) : code),
    [t],
  );
  const list = useCallback(
    (codes: readonly string[], none: string) =>
      codes.length === 0 ? none : codes.map(name).join(', '),
    [name],
  );
  return { name, list };
}

export function PageTitle({
  title,
  back,
  children,
}: {
  title: string;
  back?: { to: string; label: string };
  children?: ReactNode;
}) {
  const { t } = useTranslation('dashboard');
  return (
    <Stack gap="xs" mb="md">
      {back === undefined ? null : (
        <Anchor component={Link} to={back.to}>
          {t('common.backTo', { place: back.label })}
        </Anchor>
      )}
      <Group justify="space-between" align="flex-end">
        <Title order={1}>{title}</Title>
        {children}
      </Group>
    </Stack>
  );
}

export function Loading() {
  const { t } = useTranslation('dashboard');
  return (
    <Center py="xl">
      <Group>
        <Loader size="sm" />
        <Text>{t('common.loading')}</Text>
      </Group>
    </Center>
  );
}

export function ErrorNotice({ error }: { error: unknown }) {
  const { t } = useTranslation('dashboard');
  const message = error instanceof Error ? error.message : String(error);
  return (
    <Alert color="red" title={t('common.errorTitle')} role="alert">
      {t('common.errorBody', { message })}
    </Alert>
  );
}

/** Loading / error / content in one place for pages built on queries. */
export function QueryState({
  loading,
  error,
  children,
}: {
  loading: boolean;
  error: unknown;
  children: () => ReactNode;
}) {
  if (error !== null && error !== undefined) {
    return <ErrorNotice error={error} />;
  }
  if (loading) {
    return <Loading />;
  }
  return <>{children()}</>;
}
